import {
  createRouter,
  createWebHashHistory,
  createWebHistory,
  RouteRecordRaw,
  NavigationGuardNext,
  RouteLocationNormalized
} from "vue-router";
import { RouteEnum } from "./routeNames";
import { triggerGarbageCollection, aggressiveGarbageCollection } from '@/helpers/garbageCollector';
import { forceGlobalCleanup, getActiveComponentCount } from '@/composables/useGlobalMemoryManagement';
import { isFeatureEnabled } from '@/config/feature-flag';
import type { FeatureFlagKey } from '@/config/feature-flag';

const HEAVY_ROUTES = [
  'portfolio', 'social', 'markets', 'analyze', 
  'explore', 'marketPlace', 'events'
];

const GC_CONFIG = {
  enabled: true,
  aggressiveForHeavyRoutes: true,
  delayBeforeGC: 1000, // ms after navigation
  memoryThreshold: 500, // MB threshold for aggressive GC
  routeChangeCooldown: 30000 // ms between aggressive GC
};

let lastAggressiveGC = 0;

// Feature flag navigation guard 
const featureFlagGuard = (feature: FeatureFlagKey) => {
  return (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    if (!isFeatureEnabled(feature)) {
      console.warn(`Feature ${feature} is not enabled`);
      next({ name: 'feed' });
      return;
    }
    next();
  };
};

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "home",
    redirect: { name: "signin" },
  },
  {
    path: "/not-found",
    name: "notFound",
    component: () => import("../views/NotFound.vue"),
  },
  {
    path: "/snaptrade-callback",
    name: "snaptradeCallback",
    component: () => import("../views/SnaptradeCallback.vue"),
  },
  {
    path: "/u/:username",
    name: "publicProfileShort",
    meta: { showSideBar: false, showHeader: true },
    component: () => import("../views/private/Private.vue"),
    children: [
      {
        path: "",
        name: "publicProfileShortChild",
        meta: { showSideBar: false, showHeader: true },
        component: () => import("../views/private/ProfileScreenSSR.vue"),
      },
    ],
  },
  {
    path: "/signin",
    name: "signin",
    component: () => import("../views/public/KeycloakRedirect.vue"),
  },
  {
    path: "/public/callback",
    name: "keycloak-callback",
    beforeEnter: featureFlagGuard("KEY_CLOAK_SSO"),
    component: () => import("../views/public/CallbackHandler.vue"),
  },
  {
    path: "/signin/:id",
    name: "signinTwo",
    component: () => import("../views/public/KeycloakRedirect.vue"),
  },
  {
    path: "/signup",
    name: "signup",
    component: () => import("../views/public/KeycloakRedirect.vue"),
  },
  {
    path: "/signup/:id",
    name: "signupWaitlist",
    component: () => import("../views/public/KeycloakRedirect.vue"),
  },
  {
    path: "/resetpassword",
    name: "resetPassword",
    component: () => import("../views/public/KeycloakRedirect.vue"),
  },
  {
    path: "/approveAccess",
    name: "approveAccess",
    component: () => import("../views/public/KeycloakRedirect.vue"),
  },

  {
    path: "/callback/:platform",
    name: "platform",
    component: () => import("../views/private/callback/PlatformCallback.vue"),
    props: true,
  },

  {
    path: "/callback/x",
    name: "x",
    component: () => import("../views/private/callback/x.vue"),
    props: true,
  },

  {
    path: "/verifyemail",
    name: "verifyEmail",
    component: () => import("../views/public/KeycloakRedirect.vue"),
  },
  {
    path: "/otp-verification",
    name: "otpVerification",
    component: () => import("../views/public/KeycloakRedirect.vue"),
  },
  {
    path: "/forget-password",
    name: "forgetPassword",
    component: () => import("../views/public/KeycloakRedirect.vue"),
  },
  {
    path: "/locked",
    name: "locked",
    component: () => import("../views/public/KeycloakRedirect.vue"),
  },
  {
    path: "/account-banned",
    name: "accountBanned",
    component: () => import("../views/public/BanUser.vue"),
  },
  {
    path: "/account-deleted",
    name: "accountDeleted",
    component: () => import("../views/public/DeletedAccountUser.vue"),
  },
  {
    path: "/test",
    name: "test",
    component: () => import("../views/private/test.vue"),
  },
  {
    path: "/ticketDetail",
    name: "ticketDetail",
    component: () => import("../views/private/ticketDetail.vue"),
  },
  {
    path: "/private/_/_O_AUTH_/_twitter_v2",
    name: "twitter",
    component: () =>
      import("../components/AddShareComponent/TwitterSuccessfull.vue"),
  },
  {
    path: "/private/_O_AUTH/_linked_in",
    name: "linkedIn",
    component: () =>
      import("../components/AddShareComponent/TwitterSuccessfull.vue"),
  },
  {
    path: "/private/_O_AUTH/_reddit",
    name: "reddit",
    component: () =>
      import("../components/AddShareComponent/TwitterSuccessfull.vue"),
  },
  {
    path: "/profile",
    name: "profile",
    meta: { showSideBar: true, showHeader: true },
    component: () => import("../views/private/Private.vue"),
    redirect: { name: "feed" },
    children: [
      {
        path: "postDetail/:id",
        name: "postDetailFromId",
        meta: { showSideBar: false, showHeader: false },
        component: () => import("../views/private/postDetail.vue"),
      },
      {
        path: "publicPostDetail/:id",
        name: "publicPostDetailFromId",
        meta: { showSideBar: false, showHeader: true },
        component: () => import("../views/private/PublicPostDetail.vue"),
      },
      {
        path: "public/:username",
        name: "publicProfile",
        meta: { showSideBar: false, showHeader: true },
        component: () => import("../views/private/ProfileScreenSSR.vue"),
      },
      //   {
      //     path: "dashboard",
      //     name: RouteEnum.profileDashboard,
      //     meta: { requiresAuth: true, showSideBar: true, showHeader: true },
      //     component: () => import("../views/private/dashboard/Dashboard.vue"),
      //   },
      {
        path: "feed",
        name: "feed",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        component: () => import("../views/private/timeline/timeline.vue"),
      },
      {
        path: "markets",
        name: "markets",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        component: () => import("../views/private/markets/markets.vue"),
      },

      {
        path: "feed/:postId",
        name: "postDetail",
        meta: { showSideBar: true, showHeader: true, showComment: true },
        component: () => import("../views/private/timeline/timeline.vue"),
      },
      {
        path: "create",
        meta: {
          requiresAuth: true,
          showSideBar: false,
          showHeader: false,
          showComment: false,
        },
        name: "createProfile",
        component: () =>
          import("../views/private/create-profile/CreateProfile.vue"),
      },
      {
        path: "qr-code",
        meta: { requiresAuth: true },
        name: "qrCode",
        component: () => import("../views/private/QRCode/QRCode.vue"),
      },
      {
        path: "settings",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        name: "settings",
        component: () => import("../views/private/settings/settings.vue"),
      },

      {
        path: "analyze",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        name: "analyze",
        beforeEnter: featureFlagGuard('ANALYZE'),
        component: () => import("../views/private/analyze/analyze.vue"),
      },
      {
        path: "explore/:page?",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        name: "explore",
        component: () => import("../views/private/explore/explore.vue"),
        beforeEnter: [
          featureFlagGuard('EXPLORE'),
          (to, from, next) => {
            console.log('To Params:', to.params)
            if (!to.params.page) {
              next({ name: "explore", params: { page: "people" } });
            } else {
              next();
            }
          }
        ],
        children: [
          {
            path: "events",
            name: "events",
            meta: { requiresAuth: true, showSideBar: true, showHeader: true },
            beforeEnter: featureFlagGuard('EVENTS'),
            component: () => import("../views/private/events/EventsScreen.vue")
          }
        ]
      },
      {
        path: "explore/events/:eventId",
        name: "eventDetails",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        beforeEnter: featureFlagGuard('EVENTS'),
        component: () => import("../views/private/events/components/EventDetails.vue"),
        props: true,
      },
      {
        path: "explore/cluster/:clusterId",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        name: "exploreCluster",
        beforeEnter: featureFlagGuard('EXPLORE_CLUSTER'),
        component: () =>
          import(
            "../components/news-analytics/Clustered/NewsDetailCluster.vue"
          ),
      },
      {
        path: "marketPlace",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        name: "marketPlace",
        beforeEnter: featureFlagGuard('MARKETPLACE'),
        component: () => import("../views/private/marketPlace/marketPlace.vue"),
      },
      // {
      //   path: "social",
      //   meta: { requiresAuth: true, showSideBar: true, showHeader: true },
      //   name: "social",
      //   component: () => import("../views/private/social/social.vue"),
      // },
      {
        path: "market",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        name: "market",
        component: () => import("../views/private/market/market.vue"),
      },
      // {
      //   path: "leaderboard",
      //   meta: { requiresAuth: true, showSideBar: true, showHeader: true },
      //   name: "leaderboard",
      //   component: () => import("../views/private/leaderboard/leaderboard.vue"),
      // },
      {
        path: "articles_overview",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        name: "articles_overview",
        component: () => import("../views/private/articles/overview.vue"),
      },
      {
        path: "followers/:userId",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        name: "profileFollowers",
        component: () => import("../views/private/followers/Followers.vue"),
      },
      {
        path: "/stock/:stockId",
        name: "stockDetails",
        // component: () =>
        //   import("../views/private/stock-details/stock-details.vue"),
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        component: () => import("../views/private/stocks/stocks.vue"),
      },
      {
        path: "/crypto/:cryptoId",
        name: "cryptoDetails",
        // component: () =>
        //   import("../views/private/stock-details/stock-details.vue"),
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        component: () => import("../views/private/crypto/crypto.vue"),
      },
      {
        path: "following/:userId",
        meta: { requiresAuth: true },
        name: "profileFollowing",
        component: () => import("../views/private/following/following.vue"),
      },
      {
        path: "user/:username",
        name: RouteEnum.profile,
        component: () =>
          import("../views/private/profile-screen/ProfileScreen.vue"),
      },
      {
        path: "/stocks/:stockId",
        name: "stocks",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        component: () => import("../views/private/stocks/stocks.vue"),
      },
      {
        path: "portfolio",
        name: "portfolio",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        beforeEnter: featureFlagGuard('PORTFOLIO'),
        component: () =>
          import("../views/private/portfolio/PortfolioScreen.vue"),
        redirect: { name: "portfolio_tab_overview", params: { tab: "custom" } },
        children: [
          {
            path: ":tab",
            name: "portfolio_tab",
            redirect: {
              name: "portfolio_tab_inner",
              params: { tab: "custom" },
            },
          },
          {
            path: ":tab/overview",
            name: "portfolio_tab_overview",
            component: () =>
              import(
                "../views/private/portfolio/widgets/PortfolioOverview.vue"
              ),
          },
          {
            path: ":tab/holdings",
            name: "portfolio_tab_holdings",
            component: () =>
              import(
                "../views/private/portfolio/widgets/PortfolioHoldings.vue"
              ),
          },
          {
            path: ":tab/summary",
            name: "portfolio_tab_summary",
            component: () =>
              import(
                "../views/private/portfolio/widgets/PortfolioSummaryV2.vue"
              ),
          },
          {
            path: ":tab/orders",
            name: "portfolio_tab_orders",
            component: () =>
              import("../views/private/portfolio/widgets/PortfolioOrders.vue"),
          },
          {
            path: ":tab/analytics",
            name: "portfolio_tab_analytics",
            component: () =>
              import(
                "../views/private/portfolio/widgets/PortfolioAnalytics.vue"
              ),
          },
          // Brokerage-specific routes
          {
            path: "brokerage/overview",
            name: "portfolio_brokerage_overview",
            beforeEnter: featureFlagGuard('PORTFOLIO_BROKERAGE'),
            component: () =>
              import(
                "../views/private/portfolio/components/PortfolioBrokerageOverview.vue"
              ),
          },
          {
            path: "brokerage/holdings",
            name: "portfolio_brokerage_holdings",
            beforeEnter: featureFlagGuard('PORTFOLIO_BROKERAGE'),
            component: () =>
              import(
                "../views/private/portfolio/components/brokerage/BrokerageHoldings.vue"
              ),
          },
          {
            path: "brokerage/summary",
            name: "portfolio_brokerage_summary",
            beforeEnter: featureFlagGuard('PORTFOLIO_BROKERAGE'),
            component: () =>
              import(
                "../views/private/portfolio/components/brokerage/BrokerageSummary.vue"
              ),
          },
          {
            path: "brokerage/orders",
            name: "portfolio_brokerage_orders",
            beforeEnter: featureFlagGuard('PORTFOLIO_BROKERAGE'),
            component: () =>
              import(
                "../views/private/portfolio/components/brokerage/BrokerageOrdersTable.vue"
              ),
          },
          {
            path: "brokerage/transactions",
            name: "portfolio_brokerage_transactions",
            beforeEnter: featureFlagGuard('PORTFOLIO_BROKERAGE'),
            component: () =>
              import(
                "../views/private/portfolio/components/brokerage/BrokerageTransactions.vue"
              ),
          },
          {
            path: "brokerage/analytics",
            name: "portfolio_brokerage_analytics",
            beforeEnter: featureFlagGuard('PORTFOLIO_BROKERAGE'),
            component: () =>
              import(
                "../views/private/portfolio/widgets/PortfolioAnalytics.vue"
              ),
          },
          {
            path: "wallets/overview",
            name: "portfolio_wallets_overview",
            beforeEnter: featureFlagGuard('PORTFOLIO_WALLETS'),
            component: () =>
              import(
                "../views/private/portfolio/components/wallet/WalletOverview.vue"
              ),
          },
          {
            path: "wallets/holdings",
            name: "portfolio_wallets_holdings",
            beforeEnter: featureFlagGuard('PORTFOLIO_WALLETS'),
            component: () =>
              import(
                "../views/private/portfolio/components/wallet/WalletHoldings.vue"
              ),
          },
          {
            path: "wallets/transactions",
            name: "portfolio_wallets_transactions",
            beforeEnter: featureFlagGuard('PORTFOLIO_WALLETS'),
            component: () =>
              import(
                "../views/private/portfolio/components/wallet/WalletTransactions.vue"
              ),
          },
        ],
      },
      {
        path: "widgets",
        meta: { requiresAuth: true, showSideBar: true, showHeader: true },
        name: "widgets",
        component: () => import("../views/private/widgets/Widgets.vue"),
      },
      {
        path: "social",
        name: "social",
        meta: { showSideBar: true, showHeader: true },
        beforeEnter: featureFlagGuard('SOCIAL'),
        component: () => import("../views/private/social/social.vue"),
        redirect: { name: "socialize_leaderboard" },
        children: [
          // {
          //   path: "events",
          //   name: "events",
          //   component: () => import("../views/private/events/EventsScreen.vue"),

          // },
          {
            path: "leaderboard",
            name: "socialize_leaderboard",
            meta: { tab_name: "socialize_leaderboard" },
            beforeEnter: featureFlagGuard('LEADERBOARD'),
            component: () =>
              import("../views/private/social/new_leaderboard/leaderboard.vue"),
          },
          {
            path: "messenger",
            name: "socialize_chats",
            meta: { tab_name: "socialize_chats", hideHeaderCount: true },
            beforeEnter: featureFlagGuard('MESSENGER'),
            component: () =>
              import("../views/private/social/messenger/Messenger.vue"),
          },
          {
            path: "messenger/:chatId",
            meta: { tab_name: "socialize_chats", hideHeaderCount: true },
            name: "socialize_chats_chat",
            beforeEnter: featureFlagGuard('MESSENGER'),
            component: () =>
              import("../views/private/social/messenger/Messenger.vue"),
          },
        ],
      },
    ],
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve({ left: 0, top: 0 });
      }, 500);
    });
  },
});

router.beforeEach((to, from, next) => {
  const fromIsHeavy = HEAVY_ROUTES.some(route => from.path.includes(route));
  const toIsHeavy = HEAVY_ROUTES.some(route => to.path.includes(route));
  
  // console.log(`🔄 Route: ${from.name} → ${to.name}`);
  // console.log(`📊 Pre-nav components: ${getActiveComponentCount()}`);
  
  // Force component cleanup first
  if (fromIsHeavy && !toIsHeavy) {
    console.log('🧹 Cleaning up heavy route components');
    forceGlobalCleanup();
  }
  
  next();
});

router.afterEach((to, from) => {
  const fromIsHeavy = HEAVY_ROUTES.some(route => from.path.includes(route));
  const toIsHeavy = HEAVY_ROUTES.some(route => to.path.includes(route));
  const now = Date.now();
  
  // Schedule GC based on route type and conditions
  if (GC_CONFIG.enabled) {
    const scheduleGC = (delay, aggressive = false) => {
      setTimeout(() => {
        if (aggressive && now - lastAggressiveGC > GC_CONFIG.routeChangeCooldown) {
          // console.log('🚀 Triggering aggressive GC');
          aggressiveGarbageCollection();
          lastAggressiveGC = Date.now();
        } else {
          // console.log('🧹 Triggering standard GC');
          triggerGarbageCollection();
        }
        
        // Log post-GC stats
        setTimeout(() => {
          // console.log(`📊 Post-GC components: ${getActiveComponentCount()}`);
          logMemoryUsage('Post-GC');
        }, 500);
      }, delay);
    };
    
    // Aggressive GC when leaving heavy routes
    if (fromIsHeavy && !toIsHeavy) {
      // console.log('🎯 Heavy → Light route, scheduling aggressive GC');
      scheduleGC(GC_CONFIG.delayBeforeGC, true);
    }
    // Standard GC for other route changes
    else if (fromIsHeavy || toIsHeavy) {
      // console.log('🎯 Heavy route involved, scheduling GC');
      scheduleGC(GC_CONFIG.delayBeforeGC);
    }
    // Check memory threshold for any route change
    else if (isMemoryHigh()) {
      // console.log('🚨 High memory detected, scheduling GC');
      scheduleGC(GC_CONFIG.delayBeforeGC, true);
    }
  }
  
  // Always log memory after navigation
  setTimeout(() => {
    logMemoryUsage('After navigation');
  }, 2000);
});

// Memory monitoring utilities
function isMemoryHigh() {
  if (!performance.memory) return false;
  const usedMB = performance.memory.usedJSHeapSize / 1048576;
  return usedMB > GC_CONFIG.memoryThreshold;
}

function logMemoryUsage(context = '') {
  if (performance.memory) {
    const used = Math.round(performance.memory.usedJSHeapSize / 1048576);
    const total = Math.round(performance.memory.totalJSHeapSize / 1048576);
    const limit = Math.round(performance.memory.jsHeapSizeLimit / 1048576);
    // console.log(`💾 ${context} Memory: ${used}MB / ${total}MB (Limit: ${limit}MB)`);
  }
}

export default router;
